<template>
  <!-- Not assigned: CTA button -->
  <button
    v-if="!department && !readonly"
    type="button"
    class="dtpf-assign-btn"
    @click="$emit('assign')"
  >
    <DepartmentPathIcon class="dtpf-icon" />
    <span class="dtpf-assign-label">Assign Department</span>
  </button>

  <!-- Assigned: removable path tag -->
  <span v-else-if="department" class="dtpf-tag">
    <DepartmentPathIcon class="dtpf-icon dtpf-tag-icon" />

    <span class="dtpf-path">
      <template v-for="(seg, i) in segments">
        <span
          :key="`seg-${i}`"
          :class="{
            'dtpf-seg-ancestor': seg.kind === 'ancestor',
            'dtpf-seg-ellipsis': seg.kind === 'ellipsis',
            'dtpf-seg-leaf': seg.kind === 'leaf',
            'dtpf-seg-leaf--bold': seg.kind === 'leaf' && leafBold,
            'dtpf-seg-root-only': seg.kind === 'root-only',
          }"
        >{{ seg.text }}</span>
        <span v-if="i < segments.length - 1" :key="`sep-${i}`" class="dtpf-seg-sep">›</span>
      </template>
    </span>

    <EXTooltip
      v-if="removable && !readonly"
      class="dtpf-close-tip"
      title="Remove department"
      placement="top"
      :delay="100"
      show-on-child-focus
    >
      <button
        type="button"
        class="dtpf-close"
        aria-label="Remove department"
        @click="onRemove"
      >
        <TagCloseIcon />
      </button>
    </EXTooltip>
  </span>
</template>

<script setup>
import { computed } from 'vue';
import EXTooltip from '@components/tooltip/EXTooltip.vue';
import DepartmentPathIcon from './icons/DepartmentPathIcon.vue';
import TagCloseIcon from './icons/TagCloseIcon.vue';

const props = defineProps({
  /** Assigned department as a root→leaf path. null → "Assign Department" button state. */
  department: {
    type: Object,
    default: () => null,
  },
  /** Whether the assigned tag shows the trailing × (removable vs. read-only). */
  removable: {
    type: Boolean,
    default: true,
  },
  /** Read-only mode (Rule 8): hides the "+ Department" CTA and the × button. */
  readonly: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['remove', 'assign']);

const segments = computed(() => {
  const path = props.department?.path ?? [];
  if (path.length === 0) return [];
  if (path.length === 1) return [{ text: path[0], kind: 'root-only' }];
  if (path.length <= 3) {
    return path.map((text, i) =>
      i === path.length - 1 ? { text, kind: 'leaf' } : { text, kind: 'ancestor' },
    );
  }
  return [
    { text: path[0], kind: 'ancestor' },
    { text: '…', kind: 'ellipsis' },
    { text: path[path.length - 1], kind: 'leaf' },
  ];
});

const leafBold = computed(() => (props.department?.path?.length ?? 0) >= 3);

function onRemove() {
  if (props.department) emit('remove', { id: props.department.id });
}
</script>

<style scoped>
.dtpf-icon {
  flex-shrink: 0;
}

/* Assigned tag (pill) */
.dtpf-tag {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 2px 8px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.04);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.dtpf-tag-icon {
  color: #475467;
}

.dtpf-path {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  line-height: 18px;
  white-space: nowrap;
}

.dtpf-seg-root-only,
.dtpf-seg-ancestor,
.dtpf-seg-ellipsis {
  font-weight: 500;
  color: #475467;
}

.dtpf-seg-leaf {
  font-weight: 500;
  color: #6941c6;
}

.dtpf-seg-leaf--bold {
  font-weight: 600;
}

.dtpf-seg-sep {
  font-weight: 500;
  color: #d0d5dd;
}

.dtpf-close-tip {
  display: inline-flex;
  line-height: 0;
}

.dtpf-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  border: none;
  background: transparent;
  border-radius: 3px;
  cursor: pointer;
}

.dtpf-close:hover {
  background: #f2f4f7;
}

/* Not-assigned CTA button */
.dtpf-assign-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 28px;
  padding: 6px 10px;
  background: #7f56d9;
  border: 1px solid #7f56d9;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  color: #ffffff;
  cursor: pointer;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.dtpf-assign-btn:hover {
  background: #6941c6;
  border-color: #6941c6;
}

.dtpf-assign-label {
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;
}
</style>
